import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TextFieldComponent from '../components/TextFieldComponent';
import DropdownComponent from '../components/DropDownComponent';
import ButtonComponent from '../components/ButtonComponent';
import CheckboxComponent from '../components/CheckBoxComponent'
import constants from '../helpers/constants.json';
import moment from 'moment';
import { components } from "react-select";

import {
  addEmployerData,
  clearAddedEmployerFlag,
  clearEditedEmployerFlag,
  editEmployerData,
  getEmployeesList,
} from '../redux/actions/employerActions';
import { formatYYYYMMDDDate } from '../helpers/formattedDateTime';
import { Add } from '@mui/icons-material';
import { Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import CreateCommercialTermsModal from '../models/CreateCommercialTermsModal';
import { getCommercialTermsList } from '../redux/actions/commonActions';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  mobileNumber: Yup.string()
    .required('Mobile Number is required')
    .test(
      "Mobile Number",
      "Mobile Number must be exactly 10 digits",
      value => {
        // Skip validation if format is XXXXXX1234
        if (/^XXXXXX\d{4}$/.test(value)) {
          return true;
        }

        // Otherwise, must be exactly 10 digits
        return /^\d{10}$/.test(value);
      }),
  dob: Yup.date().required('Date of Birth is required').test(
    "DOB",
    "Employer must be 18 years old",
    value => {
      return moment().diff(moment(value), 'years') >= 18;
    }
  ),
  addressLine3: Yup.string().required('Address Line 3 is required'),
  outletType: Yup.object().required('Outlet type is required'),
  companyName: Yup.string().required('Company name is required'),
  brandName: Yup.string().required('Brand name is required'),
  commercialTermsId: Yup.object().when(['isVerified', 'isAssistedHire'], {
    is: (verified, active) => verified && active,
    then: () => Yup.object().required('Commercial Terms is required'),
    otherwise: () => Yup.object().notRequired()
  }),
  supportPeriod: Yup.number().when(['isVerified', 'isAssistedHire'], {
    is: (verified, active) => verified && active,
    then: () => Yup.number().required('Support Period is required').typeError('Entry only Number (number of days)'),
    otherwise: () => Yup.number().notRequired()
  }),
  paymentPeriod: Yup.number().when(['isVerified', 'isAssistedHire'], {
    is: (verified, active) => verified && active,
    then: () => Yup.number().required('Payment Period is required').typeError('Entry only Number (number of days)'),
    otherwise: () => Yup.number().notRequired()
  }),
  source: Yup.string().when(['isVerified', 'isAssistedHire'], {
    is: (verified, active) => verified && active,
    then: () => Yup.string().required('Source is required'),
    otherwise: () => Yup.string().notRequired()
  }),
});

const EmployerForm = ({ employerDetail, employerQueryParams, onClose }) => {
  const dispatch = useDispatch();
  const isNewEmployerCreated = useSelector((state) => state.employer.newEmployerCreated);
  const isEmployerEditedSuccess = useSelector((state) => state.employer.editEmployerSuccess);

  const commercialTerms = useSelector((state) => state.common.commercialTerms);

  const [isCreateTermsModalVisible, setIsCreateTermsModalVisible] = useState(false);

  const { gender, outletTypeList } = constants;

  const genderObject = gender.find(gender => gender?.id === employerDetail?.gender);
  const outletObject = outletTypeList.find(outletType => outletType?.id === employerDetail?.outletType)
  const commercialTermsObject = commercialTerms?.data?.find(term => term.id == employerDetail?.commercialTermsId);

  const updatedEmployerDetails = {
    ...employerDetail,
    gender: genderObject || null,
    outletType: outletObject || null,
    commercialTermsId: commercialTermsObject || null
  };


  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: employerDetail ? employerDetail.name : '',
      mobileNumber: employerDetail ? employerDetail.mobileNumber : '',
      gender: updatedEmployerDetails ? updatedEmployerDetails.gender : null,
      dob: employerDetail ? new Date(employerDetail.dob) : null,
      description: employerDetail ? employerDetail.description : '',
      outletType: updatedEmployerDetails ? updatedEmployerDetails.outletType : '',
      companyName: employerDetail ? employerDetail.companyName : '',
      brandName: employerDetail ? employerDetail.brandName : '',
      addressLine1: employerDetail ? employerDetail.addressLine1 : '',
      addressLine2: employerDetail ? employerDetail.addressLine2 : '',
      addressLine3: employerDetail ? employerDetail.addressLine3 : '',
      zomatoLink: employerDetail ? employerDetail.zomatoLink : '',
      otherLink: employerDetail ? employerDetail.otherLink : '',
      companyEmail: employerDetail ? employerDetail.companyEmail : '',
      isVerified: employerDetail ? employerDetail.verified : false,
      isAssistedHire: employerDetail ? employerDetail.assistedHire : false,
      isGigApproved: employerDetail ? employerDetail.gigApproved : false,
      commercialTermsId: updatedEmployerDetails ? updatedEmployerDetails.commercialTermsId : '',
      supportPeriod: employerDetail ? employerDetail.supportPeriod : '',
      paymentPeriod: employerDetail ? employerDetail.paymentPeriod : '',
      source: employerDetail ? employerDetail.source : ''
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      console.log(values);
      const formattedValues = {
        ...values,
        outletType: values.outletType?.id,
        gender: values.gender?.id,
        commercialTermsId: values.commercialTermsId?.id,
        dob: formatYYYYMMDDDate(values.dob),
      };

      console.log(formattedValues);
      if (employerDetail?.employerId) {
        await dispatch(editEmployerData(employerDetail.employerId, formattedValues));
      } else {
        await dispatch(addEmployerData(formattedValues));
      }
      onClose();
    },
  });

  useEffect(() => {
    dispatch(getCommercialTermsList());
  }, [dispatch]);

  useEffect(() => {
    if (isNewEmployerCreated) {
      formik.resetForm()
      dispatch(clearAddedEmployerFlag());
      dispatch(getEmployeesList(employerQueryParams))
    }
  }, [dispatch, formik, isNewEmployerCreated, employerQueryParams]);

  useEffect(() => {
    if (isEmployerEditedSuccess) {
      formik.resetForm()
      dispatch(clearEditedEmployerFlag());
      onClose();
    }
  }, [dispatch, formik, isEmployerEditedSuccess, onClose]);

  const handleCreateNewCommercialTerms = () => {
    setIsCreateTermsModalVisible(true);
  }

  const CustomOption = (props) => {
    return (
      <components.Option {...props}>
        <Box display='flex' alignItems='center'>
          <Typography fontWeight={500}>
            {props.data.name}
          </Typography>
          <Box ml={1} display="flex" gap={0.5} flexWrap="wrap">
            {props.data.slabs.map((s, i) => (
              <Chip
                key={i}
                size="small"
                sx={{
                  color: props.isSelected ? 'white' : 'default',
                  backgroundColor: props.isSelected ? 'primary.light' : 'default'
                }}
                label={`${s.percentage}% ${s.maxCtc ? `<${s.maxCtc}` : `≥${s.minCtc}`}`}
              />
            ))}
          </Box>
        </Box>
      </components.Option>
    );
  };

  const CustomSingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Box display='flex' alignItems='center'>
          <Typography fontWeight={500}>
            {props.data.name}
          </Typography>
          <Box ml={1} display="flex" gap={0.5} flexWrap="wrap">
            {props.data.slabs.map((s, i) => (
              <Chip
                key={i}
                size="small"
                sx={{
                  color: props.isSelected ? 'white' : 'default',
                  backgroundColor: props.isSelected ? 'primary.light' : 'default'
                }}
                label={`${s.percentage}% ${s.maxCtc ? `<${s.maxCtc}` : `≥${s.minCtc}`}`}
              />
            ))}
          </Box>
        </Box>
      </components.SingleValue>
    );
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <TextFieldComponent
        label="Name *"
        type="text"
        field={{
          ...formik.getFieldProps('name'),
        }}
        form={formik}
        fullWidth
        style={{ marginBottom: '16px' }}
        error={formik.touched.name && formik.errors.name}
      />

      <div style={{ marginBottom: '16px', marginTop: '16px' }}>
        <TextFieldComponent
          label="Date of Birth *"
          type="date"
          field={{
            ...formik.getFieldProps('dob'),
            onChange: formik.handleChange('dob'),
          }}
          form={formik}
          fullWidth
          style={{ marginBottom: '16px' }}
          error={formik.touched.dob && formik.errors.dob}
        />
      </div>

      <TextFieldComponent
        label="Mobile Number *"
        type="tel"
        inputmode="numeric"
        pattern="[0-9\s]{13,19}"
        disabled={employerDetail ? true : false}
        maxLength={10}
        minLength={10}
        field={{
          ...formik.getFieldProps('mobileNumber'),
          onChange: formik.handleChange('mobileNumber'),
        }}
        form={formik}
        fullWidth
        style={{ marginBottom: '16px' }}
        error={formik.touched.mobileNumber && formik.errors.mobileNumber}
      />

      <DropdownComponent
        label="Gender"
        field={{
          ...formik.getFieldProps('gender'),
          onChange: formik.handleChange('gender'),
        }}
        form={formik}
        options={gender}
        fullWidth
        style={{ marginBottom: '16px', }}
      />


      <TextFieldComponent
        label="Description"
        type="text"
        multiline={true}
        rows={4}
        field={{
          ...formik.getFieldProps('description'),
          onChange: formik.handleChange('description'),
        }}
        form={formik}
        fullWidth
        style={{ marginBottom: '16px', }}
      />

      <DropdownComponent
        label="Outlet Type *"
        field={{
          ...formik.getFieldProps('outletType'),
          onChange: formik.handleChange('outletType'),
        }}
        form={formik}
        options={outletTypeList}
        fullWidth
        style={{ marginBottom: '16px' }}
      />

      <TextFieldComponent
        label="Company Name *"
        type="text"
        field={{
          ...formik.getFieldProps('companyName'),
          onChange: formik.handleChange('companyName'),
        }}
        form={formik}
        fullWidth
        style={{ marginBottom: '16px' }}
      />

      <TextFieldComponent
        label="Brand Name *"
        type="text"
        field={{
          ...formik.getFieldProps('brandName'),
          onChange: formik.handleChange('brandName'),
        }}
        form={formik}
        fullWidth
        style={{ marginBottom: '16px' }}
      />

      <TextFieldComponent
        label="Address Line 1"
        type="text"
        field={{
          ...formik.getFieldProps('addressLine1'),
          onChange: formik.handleChange('addressLine1'),
        }}
        form={formik}
        fullWidth
        style={{ marginBottom: '16px' }}
      />

      <TextFieldComponent
        label="Address Line 2"
        type="text"
        field={{
          ...formik.getFieldProps('addressLine2'),
          onChange: formik.handleChange('addressLine2'),
        }}
        form={formik}
        fullWidth
        style={{ marginBottom: '16px' }}
      />

      <TextFieldComponent
        label="Address Line 3 *"
        type="text"
        field={{
          ...formik.getFieldProps('addressLine3'),
          onChange: formik.handleChange('addressLine3'),
        }}
        form={formik}
        fullWidth
        style={{ marginBottom: '16px' }}
      />

      <TextFieldComponent
        label="Zomato Link"
        type="text"
        field={{
          ...formik.getFieldProps('zomatoLink'),
          onChange: formik.handleChange('zomatoLink'),
        }}
        form={formik}
        fullWidth
        style={{ marginBottom: '16px' }}
      />

      <TextFieldComponent
        label="Other Link"
        type="text"
        field={{
          ...formik.getFieldProps('otherLink'),
          onChange: formik.handleChange('otherLink'),
        }}
        form={formik}
        fullWidth
        style={{ marginBottom: '16px' }}
      />

      <TextFieldComponent
        label="Company Email"
        type="email"
        field={{
          ...formik.getFieldProps('companyEmail'),
          onChange: formik.handleChange('companyEmail'),
        }}
        form={formik}
        fullWidth
        style={{ marginBottom: '16px' }}
      />

      <CheckboxComponent
        label="Verified? *"
        field={{
          ...formik.getFieldProps('isVerified'),
          onChange: () => formik.handleChange('isVerified'),
        }}
        form={formik}
        style={{ marginBottom: '16px' }}
      />

      <CheckboxComponent
        label="Assisted Hire? *"
        field={{
          ...formik.getFieldProps('isAssistedHire'),
          onChange: () => formik.handleChange('isAssistedHire'),
        }}
        form={formik}
        style={{ marginBottom: '16px' }}
      />

      {formik.values.isVerified && formik.values.isAssistedHire &&
        <>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: '16px' }}>
            <DropdownComponent
              CustomOption={CustomOption}
              CustomSingleValue={CustomSingleValue}
              label="Commercial Terms *"
              field={{
                ...formik.getFieldProps('commercialTermsId'),
                onChange: formik.handleChange('commercialTermsId'),
              }}
              form={formik}
              options={commercialTerms?.data}
              fullWidth
            />
            <Tooltip title="Create New Terms">
              <Button
                variant='outlined'
                color='primary'
                style={{ minWidth: '32px', marginLeft: '8px' }}
                onClick={handleCreateNewCommercialTerms}
              >
                <Add />
              </Button>
            </Tooltip>
          </div>

          <TextFieldComponent
            label="Support Period *"
            type="tel"
            inputmode="numeric"
            pattern="[0-9\s]{13,19}"
            field={{
              ...formik.getFieldProps('supportPeriod'),
              onChange: formik.handleChange('supportPeriod'),
            }}
            form={formik}
            fullWidth
            style={{ marginBottom: '16px' }}
          />

          <TextFieldComponent
            label="Payment Period *"
            placeholder="Days from Date of Joining"
            type="tel"
            inputmode="numeric"
            pattern="[0-9\s]{13,19}"
            field={{
              ...formik.getFieldProps('paymentPeriod'),
              onChange: formik.handleChange('paymentPeriod'),
            }}
            form={formik}
            fullWidth
            style={{ marginBottom: '16px' }}
          />

          <TextFieldComponent
            label="Source *"
            type="text"
            field={{
              ...formik.getFieldProps('source'),
              onChange: formik.handleChange('source'),
            }}
            form={formik}
            fullWidth
            style={{ marginBottom: '16px' }}
          />
        </>
      }

      <CheckboxComponent
        label="Gig Approved? *"
        field={{
          ...formik.getFieldProps('isGigApproved'),
          onChange: () => formik.handleChange('isGigApproved'),
        }}
        form={formik}
        style={{ marginBottom: '16px' }}
      />

      <ButtonComponent
        type="submit"
        label={formik.isSubmitting ? 'Submitting...' : 'Submit'}
        fullWidth
        disabled={formik.isSubmitting}
      />

      {isCreateTermsModalVisible &&
        <CreateCommercialTermsModal
          open={true}
          onClose={() => setIsCreateTermsModalVisible(false)}
        />
      }
    </form>
  );
};

export default EmployerForm;
